import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { OverviewTab } from './OverviewTab';
import { TransfersTab } from './TransfersTab';
import { ForensicTab } from './ForensicTab';
import { CounterpartiesTab } from './CounterpartiesTab';
import { IntelligenceTab } from './IntelligenceTab';
import { ActionsTab } from './ActionsTab';
import { AnalysisTab } from './AnalysisTab';
import { GranularActionsRow } from './GranularActionsRow';
import { InspectorTabStrip } from './InspectorTabStrip';
import { FindingsStrip } from './FindingsStrip';
import { NodeIdentityChips } from './NodeIdentityChips';
import { TriageCard } from '../workbench/TriageCard';
import { X, AlertTriangle, FileText, Wand2, DollarSign, Coins, Copy, ChevronDown, ChevronRight } from 'lucide-react';
import { EdgeCollapseTab } from '../workbench/EdgeCollapseTab';
import { toast } from 'sonner';
import { useDossier } from '../../hooks/useDossier';
import { useResizablePanel } from '../../hooks/useResizablePanel';
import { getNodeHealth } from '../../utils/nodeHealth';
import { NodeHealthBanner } from './NodeHealthBanner';
import { useGraphStore } from '../../stores/useGraphStore';
import { useTransactionStore } from '../../stores/useTransactionStore';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { usePanelStore } from '../../stores/usePanelStore';
import { useTriageStore, getTriageRiskScore, getIsTriageMatch } from '../../stores/useTriageStore';
import { useInvestigationStore } from '../../stores/useInvestigationStore';
import { useFilterStore } from '../../stores/useFilterStore';
import { useForensicOverlayStore } from '../../stores/useForensicOverlayStore';
import { useSearchParams } from 'react-router-dom';
import { safeStr, toErrorMessage } from '../../utils/safeStr';
import { normalizeChain } from '../../utils/canvasSync';
import { detectChainStrict } from '../../utils/chainDetect';
import { isPlaceholderNode } from '../../utils/placeholderNode';
import { clsx } from 'clsx';
import { InfoHint } from '../InfoHint';
import { section91Basis } from './section91';

/** Details disclosure state. Mirrors the width key's convention. */
const DETAILS_COLLAPSE_KEY = 'workbench-inspector-details-collapsed';

// Types from shared file
import type { NodeInspectorProps, InspectorTab } from './types';
// Re-export for external consumers (Workbench, etc.)
export type { NodeInspectorProps, InspectorTab, ActionStatuses, GranularAction, Finding, NodeHealth } from './types';

// NodeInspectorProps interface is defined in ./types.ts (see re-export above)

export const NodeInspector: React.FC<NodeInspectorProps> = (props) => {
    const {
        chain,
        onClose,
        dossierOverride
    } = props;

    // ── Read from Zustand stores (Phase 0 full) ──
    const selectedNode = useSelectionStore(s => s.selectedNode)!;
    const activeTab = useSelectionStore(s => s.activeTab) as InspectorTab;
    const setActiveTab = useSelectionStore(s => s.setActiveTab);
    const minimized = useSelectionStore(s => s.nodeDetailMinimized);
    const setMinimized = useSelectionStore(s => s.setNodeDetailMinimized);
    const txFilter = useSelectionStore(s => s.txFilter);
    const setTxFilter = useSelectionStore(s => s.setTxFilter);
    const selectedTxs = useSelectionStore(s => s.selectedTxs);
    const setSelectedTxs = useSelectionStore(s => s.setSelectedTxs);
    const toggleTxSelection = useSelectionStore(s => s.toggleTxSelection);
    const selectedEdgePair = useSelectionStore(s => s.selectedEdgePair);
    const edgeTxMap = useSelectionStore(s => s.edgeTxMap);
    const canvasTxHashes = useSelectionStore(s => s.canvasTxHashes);

    const triageData = useTriageStore(s => s.triageData);
    const triageRiskScore = getTriageRiskScore();
    const isSelectedNodeTriageMatch = getIsTriageMatch();

    const actionStatuses = usePanelStore(s => s.actionStatuses);
    const findings = usePanelStore(s => s.findings);
    const clusterLoading = usePanelStore(s => s.clusterLoading);

    const investigation = useInvestigationStore(s => s.investigation);
    const signalFamilies = investigation?.sections?.signal_assessment?.status === 'loaded'
        ? investigation.sections.signal_assessment.data?.signal_families
        : null;

    const currencyMode = useFilterStore(s => s.currencyMode);
    const setCurrencyMode = useFilterStore(s => s.setCurrencyMode);

    // 1. Setup Resizable Panel
    // v5: wider default + larger type so officers can read labels/chips without
    // squinting. Previous v4 (400/480) still truncated entity names on Transfers.
    // 440/540 keeps canvas usable; drag up to 780px for deep reading.
    const { width, isResizing, handleMouseDown } = useResizablePanel({
        storageKey: 'workbench-inspector-width-v5',
        minWidth: 440,
        maxWidth: 780,
        defaultWidth: 540,
        compactBreakpoint: 1536,
    });

    // 1b. Details disclosure.
    //
    // Everything between the utility row and the tab strip — the Inspect/Trace
    // icons, the recommended-action banner, and the identity card — is `shrink-0`
    // and never yields. Together with the panel header and the tab strip that is
    // ~500px of fixed chrome, which on a 780px-tall window left the tab content
    // about 60px: one and a half rows of Parties or Transfers. Collapsing hands
    // that height to the tab, and costs little, because every fact in the
    // identity card also appears in the Overview tab.
    //
    // Collapsing unmounts rather than hides. The chips read from Zustand stores
    // and fetch only on an explicit click, so there is no refetch on re-expand,
    // and unmounting keeps `findUiElement` honest — a display:none anchor would
    // still match its selector and send the tour spotlight to a zero-size box.
    const [detailsCollapsed, setDetailsCollapsed] = useState(() => {
        if (typeof localStorage === 'undefined') return false;
        return localStorage.getItem(DETAILS_COLLAPSE_KEY) === 'true';
    });

    const toggleDetails = useCallback(() => {
        setDetailsCollapsed(prev => {
            const next = !prev;
            try {
                localStorage.setItem(DETAILS_COLLAPSE_KEY, String(next));
            } catch {
                // Private-browsing quota errors must not break the panel.
            }
            return next;
        });
    }, []);

    // Single scroll owner for the inspector body (everything under the title bar).
    // Nested tab scrollers were tiny, sticky, and hid content below the fold.
    const bodyScrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        bodyScrollRef.current?.scrollTo({ top: 0 });
    }, [selectedNode.id, activeTab]);

    // Ghost / forensic placeholders have no real address — `ghost:<uuid>:source`
    // is a synthetic id minted from a subpoena row whose counterparty wasn't
    // disclosed. Treating it like a real address would: (a) make useDossier
    // hit the API with a non-address string (garbage in, garbage out), and
    // (b) make the back-propagation effect below enrich the node — which
    // strips `forensic: 'ghost'`, drops the dark-slate styling, and triggers
    // an ELK re-layout because the element data churned.
    //
    // This check goes through the shared utility so every consumer agrees
    // on what a placeholder is.
    const isPlaceholder = isPlaceholderNode(selectedNode);

    // 2. Setup Dossier hook — prefer node's own chain over global effectiveChain
    // (prevents TRX dossier calls for ETH addresses after tx hash search).
    // Pass null for placeholders so useDossier never fires the API.
    const dossierChain = normalizeChain(
        selectedNode.chain || detectChainStrict(selectedNode.id) || chain,
    );
    const suppressDossierFetch = isPlaceholder || props.dataMode === 'mock';
    const actualDossier = useDossier(dossierChain, suppressDossierFetch ? null : selectedNode.id);
    const dossier = dossierOverride || actualDossier;

    // 3. Propagate dossier entity data + contract status back to canvas elements
    const enrichedEntityRef = useRef<string | null>(null);
    const contractPropagatedRef = useRef<string | null>(null);
    useEffect(() => {
        if (isPlaceholder) return;  // never enrich a placeholder back into canvas
        const profile = dossier.sections?.profile;
        if (!profile || profile.status !== 'loaded') return;

        // Entity label propagation
        const entityName = profile.entity_name || profile.name;
        if (entityName && entityName !== 'Unknown') {
            if (enrichedEntityRef.current !== selectedNode.id + ':' + entityName) {
                enrichedEntityRef.current = selectedNode.id + ':' + entityName;
                props.onEntityEnriched?.(selectedNode.id, entityName, profile.entity_type || profile.type);
            }
        }

        // Contract status propagation — fires once per node when profile loads
        const isContractVal = (profile as any)?.profile?.is_contract;
        if (isContractVal !== undefined && contractPropagatedRef.current !== selectedNode.id) {
            contractPropagatedRef.current = selectedNode.id;
            props.onContractDetected?.(selectedNode.id, isContractVal);
        }
    }, [selectedNode.id, dossier.sections?.profile, props.onEntityEnriched, props.onContractDetected, isPlaceholder]);

    // Reset refs when node changes
    useEffect(() => {
        enrichedEntityRef.current = null;
        contractPropagatedRef.current = null;
    }, [selectedNode.id]);

    // Auto-switch to the Forensic tab when a forensic-decorated node is
    // selected. This is most useful for `forensic_ghost` placeholders (the
    // exchange disclosed a movement but withheld the wallet) — the standard
    // Overview / Transfers tabs render an empty state for those, while the
    // Forensic tab carries the actual signal: KYC, exchange brand, the
    // subpoena rows that name this address, and the related movements.
    //
    // The hop guards against accidental clobbering: we only switch on a
    // *node change*, not when the user manually picked another tab for the
    // same node. Once they navigate to a different node, the rule re-arms.
    const lastForensicSwitchRef = useRef<string | null>(null);
    useEffect(() => {
        const id = selectedNode?.id;
        if (!id) return;
        if (lastForensicSwitchRef.current === id) return;
        const isForensic =
            (selectedNode as any).forensic === 'ghost' ||
            (selectedNode as any).forensic === 'self' ||
            (selectedNode as any).forensic === 'counterparty' ||
            (selectedNode as any).forensic_ghost === true;
        if (!isForensic) return;
        lastForensicSwitchRef.current = id;
        setActiveTab?.('forensic');
    }, [selectedNode?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

    // Derive contract status: canvas element data (set on Omnibar load) OR dossier profile (set after click)
    const isContract: boolean =
        selectedNode.is_contract ??
        ((dossier.sections?.profile as any)?.profile?.is_contract) ??
        false;

    // Derive identity for the summary card (R-INSPECT-02)
    const nodeId = selectedNode.id;
    const nodeName = safeStr(selectedNode.entity_name) || '';
    const nodeRisk = selectedNode.risk_score ?? 0;

    // Derive node health — single source of truth for all child components
    // Also check useTransactionStore: triage may load txs there before node.tx_count is set
    const storeTxCount = useTransactionStore(s => s.getNodeTransactions(selectedNode?.id || '').length);
    const nodeHealth = useMemo(() => getNodeHealth(selectedNode, storeTxCount), [
        selectedNode?.id, selectedNode?.entity_tags, selectedNode?.tx_count,
        selectedNode?.balance, selectedNode?.expanded_transactions?.length,
        storeTxCount,
    ]);

    // Create enrichedProps that merges store-read values + callback props + dossier.
    // Child tabs (OverviewTab, TransfersTab, IntelligenceTab, ActionsTab) receive
    // the same shape they always have — zero changes needed in child tabs.
    const enrichedProps = useMemo(() => {
        return {
            ...props,
            // Re-inject store-read values so child tabs get them via spread
            selectedNode,
            activeTab,
            setActiveTab,
            minimized,
            setMinimized,
            txFilter,
            setTxFilter,
            selectedTxs,
            setSelectedTxs,
            toggleTxSelection,
            selectedEdgePair,
            onClearEdgePair: () => useSelectionStore.getState().setSelectedEdgePair(null),
            edgeTxMap,
            canvasTxHashes,
            triageData,
            triageRiskScore,
            isSelectedNodeTriageMatch,
            actionStatuses,
            findings,
            onClearFindings: () => usePanelStore.getState().clearFindings(),
            clusterLoading,
            signalFamilies,
            expandedTxs: selectedNode?.expanded_transactions || [],
            traceContext: props.traceContext ?? null,
            dossier,
            nodeHealth,
        };
    }, [props, dossier, selectedNode, activeTab, txFilter, selectedTxs, selectedEdgePair,
        edgeTxMap, canvasTxHashes, triageData, triageRiskScore, isSelectedNodeTriageMatch,
        actionStatuses, findings, clusterLoading, signalFamilies, minimized, nodeHealth,
        setActiveTab, setTxFilter, setSelectedTxs, toggleTxSelection, setMinimized]);

    // Canvas counter: "N on canvas / M total counterparties"
    const elements = useGraphStore(s => s.elements);
    const canvasCounter = useMemo(() => {
        const txs = selectedNode.expanded_transactions || [];
        if (txs.length === 0) return null;
        const nodeId = selectedNode.id;
        const counterparties = new Set<string>();
        for (const tx of txs) {
            const cp = tx.counterparty || (tx.from_address === nodeId ? tx.to_address : tx.from_address);
            if (cp && cp !== nodeId) counterparties.add(cp);
        }
        const canvasIds = new Set<string>();
        elements.forEach(el => {
            if (!el.data?.source && el.data?.id) {
                canvasIds.add(el.data.id);
                if (el.data.id.startsWith('0x') || el.data.id.startsWith('0X')) {
                    canvasIds.add(el.data.id.toLowerCase());
                }
            }
        });
        let onCanvas = 0;
        counterparties.forEach(cp => {
            if (canvasIds.has(cp) || ((cp.startsWith('0x') || cp.startsWith('0X')) && canvasIds.has(cp.toLowerCase()))) onCanvas++;
        });
        return { onCanvas, total: counterparties.size };
    }, [selectedNode.id, selectedNode.expanded_transactions, elements]);

    const forensicHasData = useForensicHasData(selectedNode, dossier);

    // Same collapse model as Sidebar: one shell, animate width (200ms), keep
    // panel content mounted (hidden) so expand/collapse stays snappy — no full
    // tree teardown / remount of heavy tabs.
    return (
        <div
            className={clsx(
                'relative bg-card border-l border-border/60 shadow-2xl shadow-black/20 z-10 flex flex-col overflow-hidden',
                !isResizing && 'transition-[width] duration-200 ease-in-out',
                isResizing && 'opacity-90 select-none',
            )}
            style={{
                width: minimized ? 44 : width,
                maxHeight: '100%',
            }}
            data-testid="wb-node-inspector"
            data-tour="node-inspector"
            data-minimized={minimized ? 'true' : 'false'}
        >
            {/* Edge tab — primary expand affordance when minimized (matches Sidebar). */}
            <EdgeCollapseTab
                side="left"
                direction={minimized ? 'expand' : 'collapse'}
                onClick={() => setMinimized?.(!minimized)}
                label={minimized ? 'Expand panel' : 'Collapse panel'}
            />

            {/* Slim rail chrome when minimized (close + vertical label). */}
            <div
                className={clsx(
                    'absolute inset-0 flex flex-col items-center py-2 gap-1',
                    minimized ? 'opacity-100' : 'opacity-0 pointer-events-none',
                )}
                aria-hidden={!minimized}
            >
                <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-muted hover:text-foreground rounded transition-colors text-muted-foreground"
                    title="Close panel"
                    tabIndex={minimized ? 0 : -1}
                >
                    <X className="w-4 h-4" />
                </button>
                <div className="mt-2 text-[10px] font-medium text-muted-foreground tracking-wide select-none [writing-mode:vertical-rl] rotate-180">
                    Node Inspector
                </div>
            </div>

            {/* Full panel — stays mounted; display:none while minimized so expand
                does not remount heavy tabs (Overview/Intel/Analysis). */}
            <div
                className={clsx('flex flex-col flex-1 min-h-0', minimized && 'hidden')}
                aria-hidden={minimized}
            >
                {/* Drag Handle (Left edge) — disabled while minimized */}
                {!minimized && (
                    <div
                        className="absolute left-0 top-0 bottom-0 w-1.5 cursor-col-resize hover:bg-primary/50 transition-colors z-20"
                        onMouseDown={handleMouseDown}
                        data-tour="inspector-resize"
                        title="Drag to resize panel"
                    />
                )}

                <div className="p-4 flex flex-col flex-1 overflow-hidden min-w-[400px]">
                    {/* Header with Minimize/Close — stays fixed; body below scrolls. */}
                    <div className="flex items-center justify-between mb-3 shrink-0">
                        <div className="flex items-center gap-2 min-w-0">
                            <h3 className="font-semibold text-lg leading-tight">Node Inspector</h3>
                            <InfoHint
                                label="What is the Node Inspector?"
                                text="Right-hand detail panel for the wallet you clicked on the Graph Canvas. Tabs below show identity, transfers, intel, and investigation tools for that address only."
                            />
                            {isContract && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary border border-dashed border-primary/50 rounded text-xs font-semibold uppercase tracking-wide">
                                    Contract
                                    <InfoHint
                                        label="What is a smart contract?"
                                        text="This address is program code on-chain (not a personal wallet). It can hold or move funds automatically — shown with a dashed blue border on the canvas."
                                        iconClassName="w-3 h-3"
                                    />
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-1.5 text-muted-foreground shrink-0">
                            <button onClick={() => setActiveTab?.('analysis')} className="flex items-center gap-1.5 px-2.5 py-1.5 hover:bg-primary/10 hover:text-primary rounded transition-colors border border-border/50 bg-secondary/50 text-xs font-medium mr-1" title="Open Analysis & Algorithms">
                                <Wand2 className="w-4 h-4" />
                                <span>Analyze</span>
                            </button>
                            <button onClick={onClose} className="p-1.5 hover:bg-muted rounded transition-colors" title="Close panel">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Single scroll owner: identity, tabs, tab content, findings, and
                        status all live in one continuous vertical flow. Nested tab
                        scrollers were tiny (~60px) and felt sticky; page-scrollbar
                        keeps the thumb visible and overscroll-y-contain stops the
                        wheel from jumping into the canvas. */}
                    <div
                        ref={bodyScrollRef}
                        data-testid="wb-inspector-body-scroll"
                        className="flex flex-col flex-1 min-h-0 overflow-y-auto overflow-x-hidden styled-scrollbar page-scrollbar overscroll-y-contain pb-4"
                    >
                        {/* Panel-wide utility row: canvas counter + value display mode.
                            The ORIG/USD toggle used to sit inside the tab row, where
                            its ~120px of `shrink-0` width pushed the last tabs out of
                            view. It is a panel-wide display preference, not a tab, so
                            it belongs here and the tab strip gets the full width. */}
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="flex items-center gap-2 min-w-0">
                                <button
                                    type="button"
                                    onClick={toggleDetails}
                                    aria-expanded={!detailsCollapsed}
                                    data-testid="wb-inspector-details-toggle"
                                    title={detailsCollapsed
                                        ? 'Show the identity card and the Inspect/Trace actions'
                                        : 'Collapse identity and actions to free vertical space in the scrollable body'}
                                    className="flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 -ml-1 rounded text-[11px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                                >
                                    {detailsCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                                    Details
                                </button>
                                <span className="text-[11px] text-muted-foreground/60 truncate">
                                    {canvasCounter && canvasCounter.total > 0
                                        ? `${canvasCounter.onCanvas} on canvas / ${canvasCounter.total} counterpart${canvasCounter.total !== 1 ? 'ies' : 'y'}`
                                        : ''}
                                </span>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <div className="flex items-center bg-secondary/50 rounded-md p-0.5 border border-border/50">
                                    <button
                                        onClick={() => setCurrencyMode('original')}
                                        className={`px-2 py-0.5 rounded text-[11px] font-bold transition-all flex items-center gap-1 ${currencyMode === 'original' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                        title="Show original crypto values"
                                    >
                                        <Coins className="w-3 h-3" />
                                        ORIG
                                    </button>
                                    <button
                                        onClick={() => setCurrencyMode('fiat')}
                                        className={`px-2 py-0.5 rounded text-[11px] font-bold transition-all flex items-center gap-1 ${currencyMode === 'fiat' ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                                        title="Show estimated USD values"
                                    >
                                        <DollarSign className="w-3 h-3" />
                                        USD
                                    </button>
                                </div>
                                <InfoHint
                                    label="Currency display"
                                    text="ORIG shows native token amounts (USDT, TRX, ETH…). USD shows approximate fiat for report writing. Switch anytime — it does not change the underlying chain data."
                                    iconClassName="w-3.5 h-3.5"
                                />
                            </div>
                        </div>

                        {/* Collapsed stand-in: one line carrying the two facts you
                            need to know which wallet you are reading — risk band
                            and address. Clicking it re-opens the full block. */}
                        {detailsCollapsed && (
                            <button
                                type="button"
                                onClick={toggleDetails}
                                data-testid="wb-inspector-details-summary"
                                title="Show the identity card and the Inspect/Trace actions"
                                className="flex items-center gap-2 mb-2 px-2.5 py-1.5 shrink-0 min-w-0 bg-secondary/30 border border-border/50 rounded-md text-left hover:bg-secondary/60 transition-colors"
                            >
                                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${nodeRisk > 0.7 ? 'bg-red-500' : nodeRisk > 0.3 ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                                <span className="font-semibold text-[13px] truncate">
                                    {nodeName && nodeName !== 'Unknown' ? nodeName : 'Unidentified Address'}
                                </span>
                                <span className="font-mono text-[11px] text-muted-foreground truncate">
                                    {nodeId.length > 18 ? `${nodeId.slice(0, 10)}…${nodeId.slice(-6)}` : nodeId}
                                </span>
                            </button>
                        )}

                        {/* Granular Actions Row — 10 per-node investigation actions with live status */}
                        {!detailsCollapsed && props.onRunGranularAction && (
                            <GranularActionsRow
                                chain={chain}
                                actionStatuses={actionStatuses}
                                onRunAction={props.onRunGranularAction}
                            />
                        )}

                        {/* Instagram / OSINT provenance (Tier-4 leads on canvas) */}
                        {!detailsCollapsed && ((selectedNode as any).osint_lead || (selectedNode as any).osint || (selectedNode as any).type === 'social_handle' || (selectedNode as any).osint_provenance) && (
                            <div className="mb-2 px-2.5 py-2 rounded-md border border-primary/30 bg-primary/10 text-[11px] space-y-1" data-testid="wb-osint-provenance">
                                <div className="font-semibold text-pink-600 dark:text-pink-400">
                                    Instagram lead — check on-chain / bank before relying
                                </div>
                                {(() => {
                                    const p = (selectedNode as any).osint_provenance || {};
                                    const surface = p.source_surface || (selectedNode as any).source_surface;
                                    const runId = p.run_id || (selectedNode as any).run_id;
                                    const conf = p.ocr_confidence;
                                    return (
                                        <div className="text-muted-foreground space-y-0.5">
                                            {surface && <div>Found in: <span className="text-foreground">{String(surface)}</span></div>}
                                            {runId && <div className="font-mono text-[10px]">Run: {String(runId).slice(0, 8)}…</div>}
                                            {typeof conf === 'number' && !Number.isNaN(conf) && (
                                                <div>OCR confidence: {Math.round(conf * 100)}%</div>
                                            )}
                                            {p.content_hash && (
                                                <div className="font-mono text-[10px] truncate" title={p.content_hash}>
                                                    Media hash: {String(p.content_hash).slice(0, 16)}…
                                                </div>
                                            )}
                                            {(selectedNode as any).type === 'address' && (
                                                <div className="pt-0.5">Use Inspect / Trace to corroborate this wallet on-chain.</div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* Recommended Action Banner — promoted from ActionsTab */}
                        {!detailsCollapsed && (() => {
                            const isSanctioned = safeStr(selectedNode.entity_category) === 'sanctioned' || (selectedNode as any).sanctioned;
                            const isExchangeOrVASP = safeStr(selectedNode.entity_type) === 'Exchange' || safeStr(selectedNode.entity_type) === 'VASP' || safeStr(selectedNode.entity_type) === 'High Risk Exchange';
                            const isHighRisk = (selectedNode.risk_score || 0) > 0.7;
                            const isMixer = safeStr(selectedNode.entity_type).toLowerCase() === 'mixer';
                            if (isSanctioned) return (
                                <div className="flex items-center gap-1.5 px-2 py-1 mb-1 bg-[hsl(var(--risk-critical))] border border-red-500/40 rounded text-[11px] text-red-400 shrink-0">
                                    <AlertTriangle className="w-3 h-3 shrink-0" />
                                    <span className="font-semibold">Escalate to FEMA/ED</span>
                                    <span className="text-muted-foreground">— Sanctioned entity</span>
                                </div>
                            );
                            // WB09: the notice banner needs an attribution that can bear it
                            // (a verified source or a provider label, or a recorded confidence),
                            // never the exchange entity type alone and never a "Brand. User" tag.
                            const s91 = isExchangeOrVASP ? section91Basis(selectedNode) : null;
                            if (isExchangeOrVASP && s91?.show) return (
                                <div
                                    className="flex items-center gap-1.5 px-2 py-1 mb-1 bg-[hsl(var(--risk-medium))] border border-amber-500/40 rounded text-[11px] text-amber-400 shrink-0"
                                    data-testid="wb-section91-banner"
                                    title={`Notice basis: ${s91.basis}`}
                                >
                                    <FileText className="w-3 h-3 shrink-0" />
                                    <span className="font-semibold">Section 91 CrPC / 94 BNSS notice</span>
                                    <span className="text-muted-foreground">— {safeStr(selectedNode.entity_name) || 'Exchange'}</span>
                                </div>
                            );
                            if (isExchangeOrVASP && s91 && !s91.show && s91.reason !== 'cluster_tag') return (
                                <div
                                    className="flex items-center gap-1.5 px-2 py-1 mb-1 bg-secondary/40 border border-border rounded text-[11px] text-muted-foreground shrink-0"
                                    data-testid="wb-section91-unverified"
                                >
                                    <FileText className="w-3 h-3 shrink-0" />
                                    <span className="font-semibold">Exchange attribution not verified</span>
                                    <span>— confirm the owner before serving a notice</span>
                                </div>
                            );
                            if (isHighRisk || isMixer) return (
                                <div className="flex items-center gap-1.5 px-2 py-1 mb-1 bg-[hsl(var(--risk-high))] border border-red-500/40 rounded text-[11px] text-red-400 shrink-0">
                                    <AlertTriangle className="w-3 h-3 shrink-0" />
                                    <span className="font-semibold">Flag for Review</span>
                                    <span className="text-muted-foreground">— High risk address</span>
                                </div>
                            );
                            return null;
                        })()}

                        {/* Node Identity Summary card (R-INSPECT-02) — now also
                            hosts the chip set previously rendered by the standalone
                            TriageRibbon. Consolidating here removes the layout-shift
                            ribbon below the omnibar and reclaims that vertical space
                            for the canvas. See VISUAL_LANGUAGE_V2.md Step 1c.
                            v5: larger type + identity hint so triage chips are readable. */}
                        {!detailsCollapsed && (
                        <div className="flex flex-col mb-3 bg-secondary/30 border border-border/50 rounded-lg p-3.5" data-tour="identity-chips">
                            <div className="flex items-center gap-2 mb-1.5 min-w-0">
                                <div className={`w-3 h-3 rounded-full shrink-0 ${nodeRisk > 0.7 ? 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]' : nodeRisk > 0.3 ? 'bg-amber-500' : 'bg-emerald-500'}`} />
                                <h3 className="font-bold text-lg leading-tight truncate min-w-0">
                                    {(selectedNode as any).is_privacy_chain && (selectedNode as any).type === 'transaction'
                                        ? `${selectedNode.chain === 'XMR' ? 'Monero' : selectedNode.chain === 'ZEC' ? 'Zcash' : selectedNode.chain === 'DASH' ? 'Dash' : selectedNode.chain || 'Privacy'} Transaction`
                                        : nodeName && nodeName !== 'Unknown' ? nodeName : 'Unidentified Address'}
                                </h3>
                                <InfoHint
                                    label="What do identity signals mean?"
                                    text="Green / amber / red dot = risk band for this wallet. Chips below are instant triage signals (entity, OFAC, nearest exchange, bridges…). Click a chip to jump to the matching tab."
                                />
                            </div>
                            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground pl-5 group mb-1">
                                <span className="cursor-pointer hover:underline break-all leading-relaxed" onClick={() => { navigator.clipboard.writeText(nodeId); toast.success('Copied address'); }}>
                                    {nodeId}
                                </span>
                                <Copy className="w-3.5 h-3.5 cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity shrink-0" onClick={() => { navigator.clipboard.writeText(nodeId); toast.success('Copied address'); }} />
                            </div>
                            <NodeIdentityChips
                                targetAddress={nodeId}
                                targetChain={selectedNode.chain || detectChainStrict(selectedNode.id) || chain || null}
                                onRiskClick={() => setActiveTab('overview')}
                                onSanctionsClick={() => setActiveTab('intelligence')}
                                onExchangeClick={() => setActiveTab('intelligence')}
                                onEscalationClick={() => setActiveTab('analysis')}
                                onBridgeClick={() => setActiveTab('intelligence')}
                                onCrossChainClick={() => setActiveTab('intelligence')}
                            />
                            {(props.onRefreshTriage || props.onTriageExpandTransactions) && (
                                <TriageCard
                                    variant="inspector"
                                    triage={isSelectedNodeTriageMatch ? triageData : null}
                                    loading={!!props.triageLoading}
                                    error={props.triageError}
                                    onRefresh={props.onRefreshTriage || (() => {})}
                                    onExpandTransactions={props.onTriageExpandTransactions || (() => setActiveTab('transfers'))}
                                    onSanctionsScreen={props.onTriageSanctionsScreen || (() => setActiveTab('intelligence'))}
                                    onAutoTrace={props.onTriageAutoTrace || (() => {})}
                                />
                            )}
                        </div>
                        )}

                        {/* Primary tab row — all seven tabs stay reachable at 440px
                            via OverflowScroller's fade + page buttons. */}
                        <InspectorTabStrip
                            activeTab={activeTab}
                            onSelect={setActiveTab}
                            txCount={storeTxCount}
                            intelLoading={dossier.loadingKeys.size > 0}
                            forensicHasData={forensicHasData}
                        />

                        {/* Connection Warning Banner */}
                        {dossier.reconnectCount > 0 && !dossier.isComplete && !dossier.hasError && (
                            <div className="mb-2 px-2 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-500 text-xs rounded flex items-center justify-between shrink-0">
                                <span>Connection unstable · Retrying ({dossier.reconnectCount}/3)...</span>
                            </div>
                        )}
                        {!isPlaceholder && dossier.hasError && (
                            <div className="mb-2 px-2 py-1 bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs rounded flex items-center justify-between shrink-0">
                                <span>Some data unavailable</span>
                                <button onClick={() => dossier.refresh()} className="hover:underline text-amber-500 font-medium">Retry</button>
                            </div>
                        )}

                        {/* Node Health Banner — real wallets only. Collapsed
                            "[ + N More ]" groups have no balance/txs by design. */}
                        {!isPlaceholder && <NodeHealthBanner health={nodeHealth} />}

                        {/* Tab content — natural height; the inspector body above is the
                            only vertical scroll owner. Activity/Analysis used to nest
                            their own overflow-y-auto regions, which left a ~60px
                            viewport and a sticky micro-scrollbar. */}
                        <div data-testid="wb-inspector-tab-surface">
                            {activeTab === 'overview' && <OverviewTab {...enrichedProps} />}
                            {activeTab === 'counterparties' && (
                                <CounterpartiesTab
                                    onAddCounterpartyToCanvas={props.onAddCounterpartyToCanvas}
                                    onRemoveFromCanvas={props.onRemoveFromCanvas}
                                    onLoadByCount={props.onLoadByCount}
                                    onLoadByDateRange={props.onLoadByDateRange}
                                    cancelDateRangeRef={props.cancelDateRangeRef}
                                    txStartDate={props.txStartDate}
                                    txEndDate={props.txEndDate}
                                    setTxStartDate={props.setTxStartDate}
                                    setTxEndDate={props.setTxEndDate}
                                    dateRangeAllLoaded={props.dateRangeAllLoaded}
                                    enrichmentStatus={props.enrichmentStatus}
                                />
                            )}
                            {(activeTab === 'transfers' || activeTab === 'activity') && <TransfersTab {...enrichedProps} />}
                            {activeTab === 'intelligence' && <IntelligenceTab {...enrichedProps} />}
                            {activeTab === 'analysis' && <AnalysisTab {...enrichedProps} />}
                            {activeTab === 'actions' && <ActionsTab {...enrichedProps} />}
                            {activeTab === 'forensic' && <ForensicTab {...enrichedProps} />}
                        </div>

                        {/* Findings Strip — append-only results from granular actions */}
                        {findings.length > 0 && (
                            <FindingsStrip
                                findings={findings}
                                onClear={() => usePanelStore.getState().clearFindings()}
                                onTabLink={setActiveTab}
                            />
                        )}

                        {/* Status footer — triage/intel loading and errors land here
                            instead of above the canvas, which was resizing on every
                            node click. */}
                        <InspectorStatusFooter />
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Small status strip pinned to the bottom of the Inspector. Shows the triage
 * loading state while a node's quick intel is being gathered, plus any triage
 * error. Zero height when idle.
 */
const InspectorStatusFooter: React.FC = () => {
    const triageLoading = useTriageStore(s => s.triageLoading);
    const triageError = useTriageStore(s => s.triageError);
    const selectedNodeId = useSelectionStore(s => s.selectedNodeId);

    if (!triageLoading && !triageError) return null;

    const short = selectedNodeId ? `${selectedNodeId.slice(0, 8)}…${selectedNodeId.slice(-4)}` : '';

    if (triageError) {
        return (
            <div className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-red-500/10 border border-red-500/30 rounded text-[11px] shrink-0">
                <AlertTriangle className="w-3 h-3 text-red-500 shrink-0" />
                <span className="text-red-600 dark:text-red-400 font-medium truncate">Triage failed: {toErrorMessage(triageError)}</span>
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2 mt-2 px-2 py-1 bg-muted/40 border border-border/50 rounded text-[11px] shrink-0">
            <span className="inline-block w-2.5 h-2.5 rounded-full border border-sky-500 border-t-transparent animate-spin" />
            <span className="text-muted-foreground truncate">
                Gathering intelligence for <span className="font-mono">{short}</span>…
            </span>
        </div>
    );
};


/**
 * True when the selected node has subpoena/forensic records — drives the pulsing
 * dot on the Forensic tab. Checks the per-case overlay store first, then falls
 * back to the legacy `forensic_records` on the dossier profile.
 */
function useForensicHasData(
    selectedNode: { id?: string; chain?: string | null },
    dossier: { sections?: { profile?: unknown } },
): boolean {
    const [searchParams] = useSearchParams();
    const caseId = searchParams.get('case_id');
    const overlay = useForensicOverlayStore(s => caseId ? s.byCase[caseId] : null);

    const address = (selectedNode?.id || '').toLowerCase();
    const chain = (selectedNode?.chain || '').toUpperCase();
    const inOverlay = !!(overlay && address && overlay.nodes.some(n =>
        (n.chain || '').toUpperCase() === chain && n.address.toLowerCase() === address
    ));
    const profile = dossier?.sections?.profile as { forensic_records?: unknown[] } | undefined;
    return inOverlay || (profile?.forensic_records?.length || 0) > 0;
}
